/**
 * Augmented Live interactive markup - shared bridge (ENG-6766 / ENG-6788).
 *
 * The selection-to-comment feature needs the same bridge script and message
 * shape in two places: the authenticated console preview
 * (`live-markup-overlay.tsx`, srcDoc iframe) and the public live shell
 * (`renderShell()` in publisher.ts, which fetches its content and renders it
 * via srcDoc). Keeping the script + the message contract here means both
 * surfaces inject byte-identical bridge code and agree on the postMessage
 * envelope - there is a single source of truth.
 *
 * The artifact body runs in a `sandbox="allow-scripts"` iframe with NO
 * `allow-same-origin`, so the parent cannot read the iframe's selection
 * directly (that would throw a SecurityError, and granting same-origin would
 * let untrusted artefact HTML reach our origin). Instead this bridge is
 * injected into the artefact and postMessages the selection (text + rect) up
 * to the parent, which anchors a small comment prompt over the iframe.
 *
 * This module is pure (strings + plain functions, no DOM, no node:*) so it is
 * safe to import from core, the webapp, and Workers alike.
 *
 * Scope: text selection -> comment (ENG-6766), inline click-to-edit (ENG-6821),
 * and right-click -> comment on any non-text element (image, background,
 * container) via the contextmenu handler (ENG-6847).
 */

/** Envelope marker stamped on every bridge message so the parent can filter. */
export const MARKUP_MARKER = '__augmentedLiveMarkup';

export interface MarkupSelectionRect {
  top: number;
  left: number;
  bottom: number;
  right: number;
  width: number;
  height: number;
}

export type MarkupBridgeMessage =
  | {
      [MARKUP_MARKER]: true;
      type: 'selection';
      text: string;
      rect: MarkupSelectionRect;
      /**
       * CSS selector for the nearest ancestor of the selection that carries a
       * stable anchor (`[data-al-id="…"]`, else `#id`). Empty string when the
       * selection has no anchored ancestor. Lets the agent target the element
       * structurally instead of by fragile text-match (ENG-6802).
       */
      target: string;
    }
  | { [MARKUP_MARKER]: true; type: 'clear' }
  // ENG-6821: a viewer saved an inline text edit. `path` is element-child indices
  // from <body> to the edited element (the server walks source_html the same way);
  // oldText is the original (concurrency guard), newText the replacement.
  //
  // ENG-6856: when `textIndex` is present, `path` points at an INLINE-ONLY
  // container (its element children are only inline-formatting tags) and the edit
  // targets the textIndex-th TEXT NODE among that element's child nodes - so the
  // text is replaced in place and the nested inline markup (spans, strong/em, br)
  // around it is byte-preserved. Absent ⇒ the legacy whole-leaf-element edit.
  | {
      [MARKUP_MARKER]: true;
      type: 'edit';
      path: number[];
      oldText: string;
      newText: string;
      textIndex?: number;
    }
  // ENG-6847: a viewer right-clicked a non-text element to comment on it. `label`
  // is a human-readable reference ('image (hero.png)', 'background image',
  // '<section> Pricing') the parent quotes in the prompt; `target` is the same
  // structural-anchor selector convention as a text selection (ENG-6802); `rect`
  // anchors the prompt over the element.
  | {
      [MARKUP_MARKER]: true;
      type: 'element';
      label: string;
      target: string;
      rect: MarkupSelectionRect;
    };

/**
 * Parent -> content control messages (ENG-6821 / ENG-6847). The shell tells the
 * content iframe whether the viewer may edit text inline and/or comment on
 * elements (only after the auth probe confirms it), and relays the save result
 * back so the content can clear/revert the editing UI.
 *
 * `enable-edit` and `enable-comment` are separate gates on purpose: the public
 * shell arms both for an authed member, but the console preview arms ONLY
 * `enable-comment` (inline edit stays a public-shell affordance, ENG-6821). The
 * right-click element-comment handler suppresses the browser's native context
 * menu, so it must stay disarmed for anon viewers - hence a flag, not always-on.
 */
export const MARKUP_CONTROL_MARKER = '__augmentedLiveMarkupControl';
export type MarkupControlMessage =
  | { [MARKUP_CONTROL_MARKER]: true; type: 'enable-edit' }
  | { [MARKUP_CONTROL_MARKER]: true; type: 'enable-comment' }
  | { [MARKUP_CONTROL_MARKER]: true; type: 'edit-result'; ok: boolean; error?: string };

/**
 * The bridge script injected into the artefact iframe. Posts the current text
 * selection (or a clear) up to the parent on mouseup; clears on scroll so a
 * stale prompt doesn't float over moved content. Uses '*' targetOrigin because
 * the parent's origin isn't known to the sandboxed (opaque-origin) frame - the
 * payload is only the user's own selected text, never a secret. The parent
 * authenticates the message by checking event.source against the iframe window.
 */
export const MARKUP_BRIDGE_SCRIPT = `<script>(function(){
  function post(m){ try { parent.postMessage(Object.assign({${MARKUP_MARKER}:true}, m), '*'); } catch(e){} }
  // Nearest ancestor carrying a stable anchor, as a CSS selector. Prefer
  // data-al-id (the durable convention) over an incidental id. '' if none.
  // Values are run through CSS.escape so a value with quotes/colons/brackets
  // can't produce a broken or injected selector.
  function esc(v){ try { return (self.CSS && self.CSS.escape) ? self.CSS.escape(v) : v; } catch(e){ return v; } }
  // Nearest anchored ancestor of an element, as a CSS selector ('' if none).
  function anchorOf(el){
    try {
      var hit = (el && el.closest) ? el.closest('[data-al-id],[id]') : null;
      if(!hit) return '';
      var dal = hit.getAttribute('data-al-id');
      if(dal) return '[data-al-id="' + esc(dal) + '"]';
      return hit.id ? ('#' + esc(hit.id)) : '';
    } catch(e){ return ''; }
  }
  function anchorFor(range){
    try {
      var node = range.commonAncestorContainer;
      var el = (node && node.nodeType === 1) ? node : (node ? node.parentElement : null);
      return anchorOf(el);
    } catch(e){ return ''; }
  }
  // Human-readable reference to a right-clicked element, kept to one line so it
  // reads cleanly as the quoted context in the comment prompt. Names images by
  // alt/filename, background-image elements by filename, otherwise tag + a short
  // text snippet so the agent knows exactly what was clicked (ENG-6847).
  function basename(u){ try { return (u||'').split('?')[0].split('#')[0].split('/').pop() || (u||''); } catch(e){ return u||''; } }
  function describeEl(el){
    try {
      var tag=(el.tagName||'').toLowerCase();
      if(tag==='img'){
        var alt=(el.getAttribute('alt')||'').trim();
        if(alt) return 'image: ' + alt;
        var src=el.getAttribute('src')||el.currentSrc||'';
        var n=basename(src);
        return n ? ('image (' + n + ')') : 'image';
      }
      var bg='';
      try { bg=(self.getComputedStyle ? self.getComputedStyle(el).backgroundImage : '') || ''; } catch(e){}
      if(bg && bg.indexOf('url(')!==-1){
        var m=bg.match(/url\\(\\s*["']?([^"')]+)["']?\\s*\\)/);
        var bn=(m && m[1]) ? basename(m[1]) : '';
        return bn ? ('background image (' + bn + ')') : 'background image';
      }
      var txt=(el.textContent||'').replace(/\\s+/g,' ').trim();
      if(tag==='svg' || (!txt && el.querySelector && el.querySelector('svg'))) return 'icon (<' + (tag||'svg') + '>)';
      if(txt) return '<' + (tag||'element') + '> ' + (txt.length>60 ? txt.slice(0,57)+'…' : txt);
      return '<' + (tag||'element') + '> element';
    } catch(e){ return 'element'; }
  }

  // ===== ENG-6821: inline click-to-edit =====
  var editEnabled = false;    // armed only after the shell confirms the viewer may edit
  var commentEnabled = false; // armed when the viewer may comment (ENG-6847 right-click)
  var editing = null;         // the element currently in edit mode
  var bar = null;             // the Save/Cancel toolbar

  // Element-child indices from <body> down to el. Counts ELEMENT children only
  // (children, not childNodes), matching how the server walks source_html; our
  // injected style/script sit at the end of <body>, so the authored content's
  // elements keep their indices.
  function pathTo(el){
    var path=[], n=el;
    while(n && n!==document.body && n.parentElement){
      path.unshift(Array.prototype.indexOf.call(n.parentElement.children, n));
      n=n.parentElement;
    }
    return path;
  }
  // Only LEAF text elements are editable. An element with element children is a
  // container - editing it would send the container's path + a flattened
  // textContent, replacing the whole subtree and destroying nested markup. Saving
  // is text-of-one-leaf only, preserving byte-fidelity everywhere else.
  function editable(el){
    if(!el || el.nodeType!==1) return false;
    if(el===document.body || el===document.documentElement) return false;
    if(el.children && el.children.length>0) return false;
    var tag=el.tagName;
    if(tag==='SCRIPT'||tag==='STYLE'||tag==='A'||tag==='BUTTON'||tag==='INPUT'||tag==='TEXTAREA'||tag==='IMG') return false;
    if(el.closest && el.closest('[data-al-noedit]')) return false;
    return ((el.textContent||'').trim().length>0);
  }
  function removeBar(){ if(bar){ try{bar.remove();}catch(e){} bar=null; } }
  // ENG-6856: inline-formatting tags whose presence inside a container still lets
  // us edit that container's bare text nodes per-node - the nested markup survives
  // because we replace only the targeted TEXT node, never the whole subtree.
  var INLINE_OK={SPAN:1,STRONG:1,EM:1,B:1,I:1,U:1,SMALL:1,MARK:1,SUB:1,SUP:1,CODE:1,BR:1,ABBR:1,WBR:1,Q:1,CITE:1,TIME:1};
  // A container is per-text-node editable when EVERY element child is an inline
  // formatting tag (so it is not a leaf, but has no block / link / control child
  // an in-place text edit could strand). <a> is deliberately absent from INLINE_OK:
  // a link child blocks editing so an edit can never clobber an href.
  function inlineOnlyEditable(el){
    if(!el || el.nodeType!==1) return false;
    if(el===document.body || el===document.documentElement) return false;
    var tag=el.tagName;
    if(tag==='SCRIPT'||tag==='STYLE'||tag==='A'||tag==='BUTTON'||tag==='INPUT'||tag==='TEXTAREA'||tag==='IMG') return false;
    if(el.closest && el.closest('[data-al-noedit]')) return false;
    var kids=el.children; if(!kids || kids.length===0) return false; // leaves use editable()
    for(var i=0;i<kids.length;i++){ if(!INLINE_OK[kids[i].tagName]) return false; }
    return ((el.textContent||'').trim().length>0);
  }
  // The text node under the pointer, so a click on bare text inside an inline-only
  // container targets exactly that run (not its inline siblings).
  function textNodeAtPoint(x,y){
    try{
      if(document.caretRangeFromPoint){ var r=document.caretRangeFromPoint(x,y); return (r && r.startContainer && r.startContainer.nodeType===3) ? r.startContainer : null; }
      if(document.caretPositionFromPoint){ var p=document.caretPositionFromPoint(x,y); return (p && p.offsetNode && p.offsetNode.nodeType===3) ? p.offsetNode : null; }
      return null;
    }catch(e){ return null; }
  }
  // Index of a text node among its parent's child TEXT nodes, in order - the same
  // counting the server uses to re-locate it in source_html (ENG-6856).
  function textIndexOf(container, tn){
    var i=-1, ns=container.childNodes;
    for(var k=0;k<ns.length;k++){ if(ns[k].nodeType===3){ i++; if(ns[k]===tn) return i; } }
    return -1;
  }
  function placeCaretEnd(el){ try{ var r=document.createRange(); r.selectNodeContents(el); r.collapse(false); var s=document.getSelection(); s.removeAllRanges(); s.addRange(r); }catch(e){} }
  // The Save/Cancel toolbar anchored above el. Shared by whole-element edits
  // (startEdit) and per-text-node edits (startEditTextNode).
  function showBar(el){
    bar=document.createElement('div');
    bar.setAttribute('data-al-noedit','1');
    bar.style.cssText='position:fixed;z-index:2147483647;display:flex;gap:6px;font:600 13px system-ui,-apple-system,sans-serif';
    var r=el.getBoundingClientRect();
    bar.style.top=Math.max(8,r.top-40)+'px'; bar.style.left=Math.max(8,r.left)+'px';
    function mk(label,bg,fn){ var b=document.createElement('button'); b.type='button'; b.textContent=label;
      b.style.cssText='border:0;border-radius:6px;padding:6px 12px;cursor:pointer;color:#fff;box-shadow:0 2px 8px rgba(0,0,0,.3);background:'+bg;
      b.addEventListener('mousedown', function(e){ e.preventDefault(); }); // keep focus/text
      b.addEventListener('click', function(e){ e.preventDefault(); fn(); }); return b; }
    bar.appendChild(mk('Save','#0b7a4b',function(){ endEdit(true); }));
    bar.appendChild(mk('Cancel','#475569',function(){ endEdit(false); }));
    document.body.appendChild(bar);
  }
  function endEdit(save){
    if(!editing) return;
    var el=editing; editing=null;
    removeBar();
    // Compare on normalized text (matches the server's whitespace-tolerant guard),
    // but send the RAW edit so intentional spacing survives and clearing a block
    // (newText '') is a real delete. The server re-scans before publishing.
    var rawNewText=el.textContent||'';
    var normNewText=rawNewText.replace(/\\s+/g,' ').trim();
    if(el.__alTextIndex!==undefined){
      // ENG-6856 per-text-node edit: el is our temporary editable wrapper span.
      // Unwrap back to a plain text node either way (a successful save's republish
      // hot-swap reloads the canonical version); on save also post the text-node
      // edit, carrying textIndex so the server replaces only that run in place.
      try{ el.parentNode.replaceChild(document.createTextNode(save?rawNewText:el.__alOldRaw), el); }catch(e){}
      if(save && normNewText!==el.__alOld){
        post({type:'edit', path:el.__alPath, textIndex:el.__alTextIndex, oldText:el.__alOld, newText:rawNewText});
      }
      return;
    }
    // Legacy whole-leaf-element edit.
    try{ el.removeAttribute('contenteditable'); el.style.outline=el.__alOut||''; }catch(e){}
    if(save && normNewText!==el.__alOld){
      post({type:'edit', path:el.__alPath, oldText:el.__alOld, newText:rawNewText});
    } else if(!save){
      try{ el.textContent=el.__alOldRaw; }catch(e){}
    }
  }
  function startEdit(el){
    if(editing) endEdit(false);
    editing=el;
    el.__alOldRaw=el.textContent;
    el.__alOld=(el.textContent||'').replace(/\\s+/g,' ').trim();
    el.__alPath=pathTo(el);
    el.__alOut=el.style.outline;
    el.style.outline='2px solid #6ee7b7';
    el.setAttribute('contenteditable','true');
    try{ el.focus(); }catch(e){}
    showBar(el);
  }
  // ENG-6856: edit one bare text node inside an inline-only container. We swap the
  // text node for a contenteditable wrapper span (so only this run is editable and
  // the inline siblings stay put) and record the container path + the text node's
  // index for the server to replace it in place. Index is computed BEFORE the swap
  // so it matches source_html's untouched text-node order.
  function startEditTextNode(tn, container){
    if(editing) endEdit(false);
    var idx=textIndexOf(container, tn);
    if(idx<0) return; // couldn't locate the node - bail, no edit
    var span=document.createElement('span');
    span.setAttribute('data-al-noedit','1');
    span.setAttribute('contenteditable','true');
    span.textContent=tn.nodeValue||'';
    span.style.outline='2px solid #6ee7b7';
    span.__alOldRaw=tn.nodeValue||'';
    span.__alOld=(tn.nodeValue||'').replace(/\\s+/g,' ').trim();
    span.__alPath=pathTo(container);
    span.__alTextIndex=idx;
    try{ tn.parentNode.replaceChild(span, tn); }catch(e){ return; }
    editing=span;
    try{ span.focus(); placeCaretEnd(span); }catch(e){}
    showBar(span);
  }
  // Hover affordance (only when armed and not mid-edit). Highlight leaves AND
  // inline-only containers (their bare text is per-node editable, ENG-6856).
  document.addEventListener('mouseover', function(e){
    if(!editEnabled||editing) return; var el=e.target;
    if(editable(el)||inlineOnlyEditable(el)){ el.style.cursor='text'; el.style.outline=el.style.outline||'1px dashed rgba(110,231,183,.6)'; el.__alHover=1; }
  });
  document.addEventListener('mouseout', function(e){
    var el=e.target; if(el&&el.__alHover&&el!==editing){ el.style.outline=''; el.__alHover=0; }
  });
  document.addEventListener('click', function(e){
    if(!editEnabled||editing) return;
    var sel=document.getSelection(); if(sel && String(sel).trim()) return; // a selection => comment flow
    var el=e.target;
    if(editable(el)){ e.preventDefault(); e.stopPropagation(); startEdit(el); return; }
    // ENG-6856: a click on bare text inside an inline-only container edits that
    // specific run. The clicked text node's parent must be the editable element
    // (a click landing inside an inline child is handled by editable() above).
    var tn=textNodeAtPoint(e.clientX, e.clientY);
    if(tn && tn.parentElement===el && inlineOnlyEditable(el) && (tn.nodeValue||'').trim()){
      e.preventDefault(); e.stopPropagation(); startEditTextNode(tn, el);
    }
  }, true);
  document.addEventListener('keydown', function(e){
    if(!editing) return;
    if(e.key==='Escape'){ e.preventDefault(); endEdit(false); }
    else if(e.key==='Enter' && !e.shiftKey){ e.preventDefault(); endEdit(true); }
  });

  document.addEventListener('mouseup', function(){
    if(editing) return; // a selection inside the editor is for the cursor, not a comment
    var sel = document.getSelection();
    var text = sel ? String(sel).trim() : '';
    if(!text || sel.rangeCount === 0){ post({type:'clear'}); return; }
    var range = sel.getRangeAt(0);
    var r = range.getBoundingClientRect();
    post({type:'selection', text:text, target:anchorFor(range), rect:{top:r.top,left:r.left,bottom:r.bottom,right:r.right,width:r.width,height:r.height}});
  });
  document.addEventListener('scroll', function(){ if(!editing) post({type:'clear'}); }, true);

  // ENG-6847: right-click any element to comment on it. Only fires once the shell
  // (or console preview) arms commenting - so the browser's native menu is left
  // intact for anon public viewers. Skip when text is selected (that's the
  // selection-comment flow) and skip our own injected UI (data-al-noedit). On a
  // hit we suppress the native menu and post the element reference + anchor + rect.
  document.addEventListener('contextmenu', function(e){
    if(!commentEnabled || editing) return;
    var sel=document.getSelection(); if(sel && String(sel).trim()) return; // selection => comment via mouseup
    var el=e.target;
    if(!el || el.nodeType!==1 || el===document.body || el===document.documentElement) return;
    if(el.closest && el.closest('[data-al-noedit]')) return; // our toolbar/prompt, not content
    e.preventDefault();
    var r=el.getBoundingClientRect();
    post({type:'element', label:describeEl(el), target:anchorOf(el), rect:{top:r.top,left:r.left,bottom:r.bottom,right:r.right,width:r.width,height:r.height}});
  });

  // Control channel from the shell (arm editing/commenting; relay save result).
  // Only the shell (window.parent) may arm - a nested iframe the artifact embeds
  // must not be able to spoof enable-edit/enable-comment (server auth still gates
  // the mutation, but keep the client gate unspoofable too).
  window.addEventListener('message', function(e){
    if(e.source!==window.parent) return;
    var d=e.data; if(!d || d['${MARKUP_CONTROL_MARKER}']!==true) return;
    if(d.type==='enable-edit'){ editEnabled=true; }
    else if(d.type==='enable-comment'){ commentEnabled=true; }
    else if(d.type==='edit-result'){
      if(d.ok){ /* republish hot-swaps the iframe; nothing to do */ }
      else { /* leave the (now non-editable) text; the published version is the source of truth */ }
    }
  });
})();</script>`;

/** Append the selection bridge to artefact HTML before it's set as srcDoc. */
export function injectMarkupBridge(content: string): string {
  return content + MARKUP_BRIDGE_SCRIPT;
}

/**
 * Compose the chat message: the selected text quoted as context, then the
 * comment, then (when the selection sat inside an anchored element) a target
 * hint so the agent edits that element by its stable id rather than guessing
 * from a text-match (ENG-6802). `target` is a CSS selector like
 * `[data-al-id="hero"]` or `#price`; falsy values are omitted.
 */
export function composeMarkupMessage(selectedText: string, comment: string, target?: string): string {
  const quoted = selectedText
    .split('\n')
    .map((line) => `> ${line}`)
    .join('\n');
  const hint = target?.trim()
    ? `\n\n(Augmented Live: the selected text is inside \`${target.trim()}\` - edit that element, and keep its id / data-al-id when you re-publish.)`
    : '';
  return `${quoted}\n\n${comment.trim()}${hint}`;
}

/**
 * Compose the chat message for an *element* comment (ENG-6847 right-click). Unlike
 * composeMarkupMessage (which quotes a text selection), this quotes a single-line
 * human-readable element reference - `image (hero.png)`, `background image`,
 * `<section> Pricing` - then the comment, then the structural target hint so the
 * agent edits that exact element by its stable id rather than guessing. `target`
 * is a CSS selector like `[data-al-id="hero"]` or `#price`; falsy values are
 * omitted.
 */
export function composeElementMarkupMessage(reference: string, comment: string, target?: string): string {
  const ref = reference.trim();
  const quoted = ref ? `> ${ref}\n\n` : '';
  const hint = target?.trim()
    ? `\n\n(Augmented Live: this comment is about the element \`${target.trim()}\` - edit that element, and keep its id / data-al-id when you re-publish.)`
    : '';
  return `${quoted}${comment.trim()}${hint}`;
}
